import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import Logo from "@/components/Logo";
import {
  LayoutDashboard, Files, FilePlus2, Users, Upload, GraduationCap, Inbox, Scale, LogOut, Bell, Menu, X,
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

function navFor(role, t) {
  const base = [{ to: "/app", label: t("app.dashboard"), icon: LayoutDashboard, end: true }];
  if (role === "individual") return [...base, { to: "/app/cases", label: t("app.cases"), icon: Files }, { to: "/app/file", label: t("app.file"), icon: FilePlus2 }];
  if (role === "business") return [...base,
    { to: "/app/cases", label: t("app.cases"), icon: Files },
    { to: "/app/file", label: t("app.file"), icon: FilePlus2 },
    { to: "/app/bulk", label: t("app.bulk"), icon: Upload },
    { to: "/app/team", label: t("app.team"), icon: Users },
  ];
  if (role === "neutral") return [...base,
    { to: "/app/cases", label: t("kpi.assigned"), icon: Files },
    { to: "/app/course", label: t("app.course"), icon: GraduationCap },
  ];
  if (role === "admin") return [...base,
    { to: "/app/cases", label: t("app.cases"), icon: Files },
    { to: "/app/admin/neutrals", label: t("app.neutrals"), icon: Scale },
    { to: "/app/admin/users", label: t("app.users"), icon: Users },
    { to: "/app/admin/leads", label: t("app.leads"), icon: Inbox },
  ];
  return base;
}

const ROLE_BADGE = { individual: "Individual", business: "Business", neutral: "Mediator/Arbitrator", admin: "Administrator" };

function SidebarContent({ links, onNav, t }) {
  return (
    <>
      <div className="p-5 border-b hairline flex items-center justify-between">
        <Logo />
        {onNav && (
          <button onClick={onNav} className="lg:hidden text-ink/60 hover:text-ink p-1" aria-label="Close menu" data-testid="mobile-nav-close">
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
      <div className="px-5 pt-5 pb-2 eyebrow">Workspace</div>
      <nav className="px-3 pb-3 flex-1 space-y-0.5">
        {links.map((l) => (
          <NavLink
            key={l.to} to={l.to} end={l.end}
            onClick={onNav}
            data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 text-[13px] transition-colors ${
                isActive ? "bg-court text-sand" : "text-ink/75 hover:bg-stone/15"
              }`
            }
            style={{ borderRadius: 3 }}
          >
            <l.icon className="h-4 w-4" /> {l.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-5 text-[11px] font-mono tracking-[0.10em] uppercase text-stone border-t hairline leading-relaxed">
        eSulah is a technology platform facilitating ODR. It is not a law firm.
      </div>
    </>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const links = navFor(user.role, t);

  // close mobile drawer on route change
  React.useEffect(() => { setOpen(false); }, [loc.pathname]);

  const onLogout = async () => { await logout(); nav("/login", { replace: true }); };
  const initials = (user.name || "U").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-sand flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r hairline bg-sand-deep">
        <SidebarContent links={links} t={t} />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-ink/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-sand-deep flex flex-col border-r hairline">
            <SidebarContent links={links} onNav={() => setOpen(false)} t={t} />
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b hairline bg-sand flex items-center px-4 sm:px-6 lg:px-10 justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setOpen(true)}
              className="lg:hidden text-ink/65 hover:text-ink p-2 -ml-2"
              aria-label="Open menu"
              data-testid="mobile-nav-toggle"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <div className="eyebrow truncate">{ROLE_BADGE[user.role]} · {t("app.welcome")}</div>
              <div className="font-display text-court mt-0.5 truncate" style={{ fontWeight: 400 }}>{user.name?.split(" ")[0]}</div>
            </div>
          </div>
          <div className="flex items-center gap-3 sm:gap-4 shrink-0">
            <button
              data-testid="lang-toggle"
              onClick={() => setLang(lang === "en" ? "hi" : "en")}
              className="font-mono text-[11px] tracking-[0.12em] uppercase text-ink/55 hover:text-ink hidden sm:inline-block"
            >
              {lang === "en" ? "EN · हिं" : "हिं · EN"}
            </button>
            <button className="text-ink/55 hover:text-ink" data-testid="notifications-bell" aria-label="Notifications">
              <Bell className="h-4 w-4" />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 sm:gap-3 px-1.5 sm:px-2 py-1 hover:bg-stone/15 transition-colors" data-testid="user-menu" style={{ borderRadius: 3 }}>
                  <div className="h-8 w-8 bg-court text-sand grid place-items-center text-[11px] font-medium shrink-0" style={{ borderRadius: 3 }}>{initials}</div>
                  <div className="text-[13px] text-ink hidden sm:block truncate max-w-[140px]">{user.name}</div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none border-stone">
                <DropdownMenuLabel className="font-mono text-[10px] tracking-[0.12em] uppercase">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => setLang(lang === "en" ? "hi" : "en")} className="sm:hidden">
                  {lang === "en" ? "हिंदी" : "English"}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={onLogout} data-testid="menu-logout"><LogOut className="h-4 w-4 mr-2" /> {t("app.logout")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-10 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
